import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

interface LoginPageProps {
  onLoginSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const user = await authService.verifyToken();
      if (user) {
        navigate('/admin/franconnect', { replace: true });
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.login({ username, password });
      
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      
      navigate('/admin/franconnect', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #00B1E1 0%, #008BB3 100%)',
        }}
      >
        <Typography sx={{ color: 'white' }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: '"Outfit", sans-serif',
      }}
    >
      {/* Add Outfit font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      {/* Left Panel - Brand */}
      <Box
        sx={{
          width: { xs: '0', md: '50%' },
          background: 'linear-gradient(135deg, #00B1E1 0%, #008BB3 50%, #006A8A 100%)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, rgba(0, 0, 0, 0.1) 0%, transparent 70%)
            `,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            animation: 'float 6s ease-in-out infinite',
          },
        }}
      >
        {/* Animated circles */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            left: '15%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            animation: 'float 8s ease-in-out infinite reverse',
          }}
        />
        
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 6 }}>
          {/* Logo */}
          <Box
            component="img"
            src="/burn-new-logo.svg"
            alt="Burn Boot Camp"
            onError={(e: any) => {
              e.target.src = 'https://innovation.dxfactor.com/burn-image-approval-tool/burn-new-logo.png';
            }}
            sx={{
              maxWidth: 320,
              mb: 5,
              mx: 'auto',
              display: 'block',
              filter: 'brightness(0) invert(1)',
              animation: 'fadeInUp 0.8s ease-out',
            }}
          />
          
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 700,
              mb: 2,
              fontFamily: '"Outfit", sans-serif',
              animation: 'fadeInUp 0.8s ease-out 0.2s both',
            }}
          >
            BurnGPT Admin
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontWeight: 400,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              fontFamily: '"Outfit", sans-serif',
              animation: 'fadeInUp 0.8s ease-out 0.4s both',
            }}
          >
            AI-powered assistant for Burn Boot Camp franchise operations and training materials
          </Typography>

          {/* Feature pills */}
          <Stack direction="row" spacing={2} sx={{ mt: 5, justifyContent: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            {['FranConnect', 'Widen Media', 'Litmos'].map((feature, index) => (
              <Box
                key={feature}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  fontFamily: '"Outfit", sans-serif',
                  animation: `fadeInUp 0.8s ease-out ${0.6 + index * 0.1}s both`,
                }}
              >
                {feature}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Right Panel - Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#FFFFFF',
          position: 'relative',
        }}
      >
        {/* Mobile logo */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            top: 32,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <Box
            component="img"
            src="/burn-new-logo.svg"
            alt="Burn Boot Camp"
            onError={(e: any) => {
              e.target.src = 'https://innovation.dxfactor.com/burn-image-approval-tool/burn-new-logo.png';
            }}
            sx={{ width: 180 }}
          />
        </Box>

        <Container maxWidth="sm" sx={{ py: { xs: 10, md: 4 } }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'visible',
              bgcolor: 'transparent',
              boxShadow: 'none',
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    color: '#1A1A2E',
                    fontWeight: 800,
                    fontSize: { xs: '1.75rem', sm: '2.25rem' },
                    fontFamily: '"Outfit", sans-serif',
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '1rem',
                    fontFamily: '"Outfit", sans-serif',
                  }}
                >
                  Sign in to manage BurnGPT
                </Typography>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 3,
                    '& .MuiAlert-icon': { alignItems: 'center' },
                  }}
                  onClose={() => setError('')}
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    label="Email"
                    type="email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    fullWidth
                    variant="outlined"
                    placeholder="Enter your email"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#F8FAFC',
                        fontFamily: '"Outfit", sans-serif',
                      },
                      '& .MuiInputLabel-root': {
                        fontFamily: '"Outfit", sans-serif',
                      },
                    }}
                  />

                  <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    variant="outlined"
                    placeholder="••••••••"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            disabled={loading}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon sx={{ color: 'text.secondary' }} />
                            ) : (
                              <VisibilityOutlinedIcon sx={{ color: 'text.secondary' }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#F8FAFC',
                        fontFamily: '"Outfit", sans-serif',
                      },
                      '& .MuiInputLabel-root': {
                        fontFamily: '"Outfit", sans-serif',
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || !username || !password}
                    sx={{
                      py: 1.75,
                      fontSize: '1rem',
                      mt: 2,
                      fontFamily: '"Outfit", sans-serif',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #00B1E1 0%, #008BB3 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #33C1E7 0%, #00B1E1 100%)',
                        transform: 'translateY(-1px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&:disabled': {
                        background: '#ccc',
                      },
                    }}
                  >
                    {loading ? 'Please wait...' : 'Sign In'}
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>

          {/* Powered by DXFactor */}
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ 
                color: 'text.secondary', 
                opacity: 0.7,
                fontFamily: '"Outfit", sans-serif',
              }}
            >
              Powered by
            </Typography>
            <Box
              component="img"
              src="/dx-factor-logo.svg"
              alt="DXFactor LLC"
              sx={{
                height: 20,
                opacity: 0.7,
              }}
              onError={(e: any) => {
                // Fallback to text if logo doesn't load
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span style="color: #666; font-family: Outfit, sans-serif; font-size: 0.875rem; opacity: 0.7;">Powered by DXFactor LLC</span>';
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-20px); }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default LoginPage;
